import React, { useState, useEffect, useMemo } from 'react';
import {
    Slider,
} from '@material-ui/core';
import { TimePicker } from '../../../../common-client2/src/components';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getPartialShiftSliderProps } from '../../../utils';
import { getSliderThresholds } from '../../../../services/Slider/SliderThreshold';
import { getDateFormatFromDiff, getDisabledPartialPeriods } from '../../../../services/Slider/PartialSliderHelper';
import { isSelectionAlreadyTaken } from '../../../../services/Slider/SliderSelectionValidation';
import { handleTextInputChange } from './SliderPartialRequest.helper';
import { getUserFormats } from '../../../../common-client2/src/services/ukgDateTime';

import './SliderPartialRequest.scss';

/**
 * @typedef {import('../../../../services/Slider/PartialSliderHelper').StoreData} StoreData
 * @typedef {import('../../../../services/Slider/SliderThreshold').PartialOfferWithLabel} PartialOfferWithLabel
 * @typedef {import('../../../../services/Slider/SliderSelectionValidation').SliderSelection} SliderSelection
 * @typedef {import('../../../../services/Slider/SliderThreshold/SliderThreshold').ShiftRequestRange } ShiftRequestRange
 * @typedef {import('../../../../services/Slider/SliderThreshold/SliderThreshold').PartialOffer } PartialOffer
 * @typedef {(evt: import('react').ChangeEvent<{}>, val: number|number[], *[]) => any} IHandleChange
 * @typedef {(takenPartialOffer: PartialOfferWithLabel) => unknown} IOnSelectionInvalid
 * @typedef {import('moment-timezone').Moment} Moment
 *
 * @typedef {{
 *  shifts: *[];
 *  storeData: StoreData;
 *  handleChange: IHandleChange;
 *  onSelectionInvalid: IOnSelectionInvalid;
 *  requestType: RequestType;
 *  selectedPartialStartDate: Moment?,
 *  selectedPartialEndDate: Moment?,
 *  isReadOnly: boolean?;
 * }} SliderPartialRequestProps
 *
 * @typedef {{
 *  value: number;
 *  label: string | null;
 * }} SliderMark
 */

/** @type {PartialOfferWithLabel} */
const initialIsTakenState = {};

const noop = () => { };

/**
 * @param {ShiftRequestRange} shiftRequestRange
 * @param {SliderSelection} selection
 * @param {SliderMark[]} marks
 */
const getPartialLabels = (shiftRequestRange, selection, marks) => {
    if (!Object.keys(shiftRequestRange || {}).length || !selection.length || !marks.length) {
        return [];
    }

    return marks.map((m) => ({
        ...m,
        ...(
            selection.includes(m.value)
                ? { label: getDateFormatFromDiff(shiftRequestRange.startDateTime, m.value) }
                : {}
        ),
    }));
};

/** @param {SliderPartialRequestProps} param0 */
const SliderPartialRequest = ({
    storeData = {},
    shifts,
    handleChange = noop,
    onSelectionInvalid = noop,
    requestType,
    selectedPartialStartDate = null,
    selectedPartialEndDate = null,
    isReadOnly = false,
}) => {
    if (!shifts?.length) {
        return <></>;
    }

    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'billboard' });

    const [shift] = shifts;

    const shiftRequestRange = {
        id: shift.id,
        startDateTime: new Date(shift.startDateTime),
        endDateTime: new Date(shift.endDateTime),
    }
    const allPartialShiftOffers = useMemo(() => getDisabledPartialPeriods(shiftRequestRange, storeData), []);

    let partialShiftSliderProps, defaultValues;
    if (isReadOnly && selectedPartialStartDate && selectedPartialEndDate) { // Summary page - view partial shift selection from previous step
        partialShiftSliderProps = getPartialShiftSliderProps(shift, selectedPartialStartDate, selectedPartialEndDate);
        defaultValues = partialShiftSliderProps.defaultValue;
    }
    else {
        partialShiftSliderProps = getPartialShiftSliderProps(shift, moment.utc(shift.startDateTime), moment.utc(shift.endDateTime));
        const { min, max } = getSliderThresholds(shiftRequestRange, allPartialShiftOffers);
        defaultValues = [min, max];
    }

    const { marks, step, max: maxInSpectrum } = partialShiftSliderProps;

    const [value, setValue] = useState(defaultValues);

    /**
     * @type {[boolean|PartialOfferWithLabel, import('react').Dispatch<import('react').SetStateAction<boolean|PartialOfferWithLabel>>]}
     */
    const [takenPartialOffer, setTakenPartialOffer] = useState(initialIsTakenState);

    const marksWithPartialLabels = getPartialLabels(shiftRequestRange, defaultValues, marks);

    useEffect(() => {
        if (isReadOnly) {
            handleChange = noop;
        }
        handleChange(() => { }, defaultValues, shifts, requestType);
    }, []);

    /**
     * @param {SliderSelection} selection
     */
    const validate = (selection) => {
        const result = isSelectionAlreadyTaken(shiftRequestRange, selection, allPartialShiftOffers)
        const isTaken = !!result;

        if (isTaken) {
            onSelectionInvalid(result);
        }

        setTakenPartialOffer(result);
    };

    /**
     * 
     * @param {*} e 
     * @param {number} val 
     */
    const onInputChange = (e, val) => {
        setValue(val);
        handleChange(e, val, shifts, requestType);
        validate(val);
    }

    // Determine if user's time format uses meridiem (AM/PM)
    const userTimeFormat = getUserFormats().TimeFormat || '';
    return (
        <>
            <div className='slider-partial-request'>
                <Slider
                    marks={marksWithPartialLabels}
                    // min={min}
                    max={maxInSpectrum}
                    onChange={onInputChange}
                    step={step}
                    value={value}
                    valueLabelDisplay="off"
                    disabled={isReadOnly}
                />
                {
                    !!Object.keys(takenPartialOffer).length && (
                        <div className="validation-error">
                            <span>
                                {t("selectionNotAvailable", { from: takenPartialOffer.fromLabel, to: takenPartialOffer.toLabel })}
                            </span>
                        </div>
                    )
                }
            </div>
            <div className="DrawerStartTimeSelectionInputContainer">
                <div className="DrawerStartTimeSelectionInput">
                    <TimePicker
                        value={moment(shift.startDateTime).add(value[0], 'm').toDate()}
                        onSubmit={(date, val) => handleTextInputChange(0, val, onInputChange, shift.startDateTime, shift.endDateTime, value)}
                        minuteStep={15}
                        minDate={shiftRequestRange.startDateTime}
                        maxDate={new Date(Math.min(shiftRequestRange.endDateTime, moment(shift.startDateTime).add(value[1], 'm').toDate()))}
                        timeFormat={userTimeFormat}
                    />
                </div>
                <div className="DrawerStartTimeSelectionInput">
                    <TimePicker
                        value={moment(shift.startDateTime).add(value[1], 'm').toDate()}
                        onSubmit={(date, val) => handleTextInputChange(1, val, onInputChange, shift.startDateTime, shift.endDateTime, value)}
                        minuteStep={15}
                        minDate={new Date(Math.max(shiftRequestRange.startDateTime, moment(shift.startDateTime).add(value[0], 'm').toDate()))}
                        maxDate={shiftRequestRange.endDateTime}
                        timeFormat={userTimeFormat}
                    />
                </div>
            </div>
        </>
    )
}

export default SliderPartialRequest;
